import { useState } from 'react'
import type { DerivedDC } from '@/api/distributionCenters'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { DcPercentInput } from './DcAllocationGrid'
import { dcUnits, equalDcSplit, type DcSplit } from './dc-model'

export function DcSplitDialog({
  centers,
  count,
  description,
  initial,
  onClose,
  onApply,
}: {
  centers: DerivedDC[]
  count: number
  description: string
  initial: DcSplit
  onClose: () => void
  onApply: (split: DcSplit) => void
}) {
  const [split, setSplit] = useState(initial)
  const total = Object.values(split).reduce((sum, value) => sum + value, 0)
  const balanced = Math.abs(total - 100) < 1e-7
  return (
    <Dialog
      open
      onOpenChange={open => {
        if (!open) onClose()
      }}
    >
      <DialogContent className="max-w-[min(480px,calc(100vw-32px))] overflow-y-auto bg-card">
        <DialogHeader>
          <DialogTitle>Copy allocation to other waves</DialogTitle>
          <DialogDescription>
            {description} Existing percentages in this scope will be replaced.
          </DialogDescription>
        </DialogHeader>
        <div className="divide-y rounded-md border px-3">
          {centers.map(dc => (
            <div
              key={dc.id}
              className="flex items-center justify-between gap-4 py-2"
            >
              <span className="text-sm font-medium">{dc.code}</span>
              <div className="w-28">
                <DcPercentInput
                  label={`${dc.code} bulk allocation percent`}
                  value={split[dc.id] ?? 0}
                  onChange={value => setSplit({ ...split, [dc.id]: value })}
                />
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSplit(equalDcSplit(centers))}
          >
            Split equally
          </Button>
          <span
            role="status"
            className={`text-sm font-semibold ${balanced ? 'text-emerald-700' : 'text-destructive'}`}
          >
            {dcUnits(total)}% / 100%
          </span>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!balanced || !count} onClick={() => onApply(split)}>
            Apply to {count} {count === 1 ? 'wave' : 'waves'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
